import { z } from 'zod';
import { tempoApi } from '@/common/api/tempoApi';
import { TempoApiError } from '@/common/api/tempoApiTypes';
import { isHttpError } from '@/common/utils/isHttpError';
import type {
  AgenticRun,
  Persona,
  RunSummary,
  SignalScope,
  TriggerRunResponse,
} from '@/features/agentic-insights/components/data/types';
import {
  personasResponseSchema,
  runSchema,
  runSummarySchema,
  triggerRunResponseSchema,
} from '@/features/agentic-insights/components/data/types';

/**
 * The mounted SIR route, which still carries the pre-rename name. NEX-16067 renames the backend
 * folder to agentic-insights; if the `.route()` path in apps/sir/src/app.ts is renamed with it, this
 * single constant is the only thing the UI needs changed.
 */
const AGENTIC_API_BASE = 'api/sir/v1/agentic-recommendations';

/**
 * A 404 is the expected response for any loop with no stored run — the normal state until someone
 * triggers one with `triggerRun`. It is a data state, not an error.
 */
const getLatestRun = async (loopId: number): Promise<AgenticRun | null> => {
  try {
    const response = await tempoApi()
      .get(`${AGENTIC_API_BASE}/loops/${loopId}/runs/latest`)
      .json<unknown>();

    return runSchema.parse(response);
  } catch (error) {
    if (isHttpError(error, 404)) {
      return null;
    }
    throw error;
  }
};

const runListResponseSchema = z.object({ runs: z.array(runSummarySchema) });

const listRuns = async (loopId: number): Promise<RunSummary[]> => {
  const response = await tempoApi().get(`${AGENTIC_API_BASE}/loops/${loopId}/runs`).json<unknown>();

  return runListResponseSchema.parse(response).runs;
};

/**
 * A 404 is the expected response for a run id that has been GC'd or never existed under this loop —
 * a data state, not an error, same as `getLatestRun`.
 */
const getRun = async (loopId: number, runId: string): Promise<AgenticRun | null> => {
  try {
    const response = await tempoApi()
      .get(`${AGENTIC_API_BASE}/loops/${loopId}/runs/${runId}`)
      .json<unknown>();

    return runSchema.parse(response);
  } catch (error) {
    if (isHttpError(error, 404)) {
      return null;
    }
    throw error;
  }
};

/**
 * The run's raw LLM transcript, for expert mode's download. Returned as `unknown` and deliberately
 * not parsed: it is an opaque debugging blob whose only consumer is a file the developer opens, so a
 * zod schema here would be a second definition to keep in step with the backend's for no gain. A 404
 * means the run recorded none — transcripts disabled, or a run older than the feature — which is a
 * data state the button reports, not an error.
 */
const getTranscript = async (loopId: number, runId: string): Promise<unknown | null> => {
  try {
    return await tempoApi()
      .get(`${AGENTIC_API_BASE}/loops/${loopId}/runs/${runId}/transcript`)
      .json<unknown>();
  } catch (error) {
    if (isHttpError(error, 404)) {
      return null;
    }
    throw error;
  }
};

/**
 * Empty means the org has never configured roles — the dialog seeds its built-in defaults from
 * that. There is no 404 here; the backend always answers with a list.
 */
const getPersonas = async (): Promise<Persona[]> => {
  const response = await tempoApi().get(`${AGENTIC_API_BASE}/personas`).json<unknown>();

  return personasResponseSchema.parse(response).personas;
};

const savePersonas = async (personas: Persona[]): Promise<Persona[]> => {
  const response = await tempoApi()
    .put(`${AGENTIC_API_BASE}/personas`, { json: { personas } })
    .json<unknown>();

  return personasResponseSchema.parse(response).personas;
};

/**
 * Thrown when the backend's per-loop interlock is already held — a distinct, typed shape (carrying
 * `activeRunId`) rather than a generic API error, so the page can offer "view the run in progress"
 * instead of the catch-all "could not start the run" alert.
 */
export class RunAlreadyActiveError extends Error {
  readonly activeRunId: string;
  constructor(activeRunId: string) {
    super('A run is already active for this loop');
    this.name = 'RunAlreadyActiveError';
    this.activeRunId = activeRunId;
  }
}

/**
 * Thrown when the backend reports that the org has no roles configured — a distinct, typed shape
 * so the page can offer "configure roles" instead of the catch-all "could not start the run" alert.
 */
export class NoRolesConfiguredError extends Error {
  constructor() {
    super('No roles are configured for this organization');
    this.name = 'NoRolesConfiguredError';
  }
}

/**
 * Starts a run and returns as soon as the backend has stored a `running` stub — it does not wait for
 * the agent loop, which takes minutes. Progress is observed by polling `getLatestRun` until its
 * status leaves `running`; see useAgenticRun.
 */
const triggerRun = async (
  loopId: number,
  signalScope: SignalScope = 'curated',
): Promise<TriggerRunResponse> => {
  try {
    const response = await tempoApi()
      .post(`${AGENTIC_API_BASE}/loops/${loopId}/runs`, { json: { signalScope } })
      .json<unknown>();

    return triggerRunResponseSchema.parse(response);
  } catch (error) {
    if (error instanceof TempoApiError && error.response.status === 409) {
      if (error.error.code === 'RUN_ALREADY_ACTIVE') {
        const activeRunId = 'meta' in error.error ? error.error.meta?.activeRunId : undefined;
        throw new RunAlreadyActiveError(typeof activeRunId === 'string' ? activeRunId : 'unknown');
      }
      if (error.error.code === 'NO_ROLES_CONFIGURED') {
        throw new NoRolesConfiguredError();
      }
    }
    throw error;
  }
};

export const AgenticInsightsApi = {
  getLatestRun,
  listRuns,
  getRun,
  getTranscript,
  getPersonas,
  savePersonas,
  triggerRun,
};
